
import { useIsMobile } from "@/hooks/use-mobile";
import PartnersCarousel from "@/components/products/PartnersCarousel";
import { Link } from "react-router-dom";
import { PienlainatPromotion } from "@/components/loan/PienlainatPromotion";
import { YrityslainatPromotion } from "@/components/loan/YrityslainatPromotion";
import { LainaCta } from "@/components/common/LainaCta";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute } from "@/utils/enhancedSeoUtils";
import { 
  Home, 
  Car, 
  Ship, 
  Building2, 
  PiggyBank, 
  CreditCard, 
  Hammer,
  TreePine,
  Heart,
  GraduationCap,
  Caravan,
  Calculator,
  Zap,
  Briefcase,
  Wrench,
  Bike,
  Plane,
  MapPin,
  DollarSign
} from "lucide-react";

interface LoanProduct {
  title: string;
  description: string;
  path: string;
  icon: React.ComponentType<any>;
  isPopular?: boolean;
  isNew?: boolean;
  minAmount?: string;
  maxAmount?: string;
}

interface LoanCategory {
  title: string;
  description: string;
  products: LoanProduct[];
}

const LainatuotteetPage = () => {
  const isMobile = useIsMobile();
  const seoData = getEnhancedSeoDataForRoute("/lainatuotteet");

  const loanCategories: LoanCategory[] = [
    {
      title: "Henkilökohtaiset lainat",
      description: "Yleiskäyttöiset lainat arkipäivän tarpeisiin",
      products: [
        {
          title: "Kulutusluotto",
          description: "Joustavaa rahoitusta arkipäivän tarpeisiin ilman vakuuksia",
          path: "/lainat/kulutusluotto",
          icon: PiggyBank,
          isPopular: true,
          minAmount: "1 000€",
          maxAmount: "50 000€"
        },
        {
          title: "Pikalaina",
          description: "Nopea rahoitus yllättäviin menoihin",
          path: "/lainat/pikalaina", 
          icon: Zap,
          minAmount: "100€",
          maxAmount: "5 000€"
        },
        {
          title: "Yhdistelylaina",
          description: "Yhdistä kaikki lainasi yhdeksi edulliseksi kokonaisuudeksi",
          path: "/lainat/yhdistelylaina",
          icon: DollarSign,
          minAmount: "5 000€",
          maxAmount: "100 000€"
        },
        {
          title: "Joustoluotto",
          description: "Käytä ja maksa takaisin joustavasti tarpeen mukaan",
          path: "/lainat/joustoluotto",
          icon: CreditCard,
          minAmount: "500€",
          maxAmount: "20 000€"
        }
      ]
    },
    {
      title: "Ajoneuvolainat",
      description: "Rahoitusta kulkuneuvojen hankintaan",
      products: [
        {
          title: "Autolaina",
          description: "Edullista autorahoitusta uusiin ja käytettyihin autoihin",
          path: "/lainat/autolaina",
          icon: Car,
          isPopular: true,
          minAmount: "5 000€",
          maxAmount: "100 000€"
        },
        {
          title: "Moottoripyörälaina",
          description: "Rahoitusta moottoripyörän ostoon",
          path: "/lainat/moottoripyoralaina",
          icon: Bike,
          minAmount: "2 000€",
          maxAmount: "30 000€"
        },
        {
          title: "Caravanrahoitus",
          description: "Rahoitusta asuntovaunun tai matkailuauton ostoon",
          path: "/lainat/caravanrahoitus",
          icon: Caravan,
          minAmount: "10 000€",
          maxAmount: "150 000€"
        }
      ]
    },
    {
      title: "Harrastus- ja vapaa-ajan lainat",
      description: "Rahoitusta harrastuksiin ja elämyksiin",
      products: [
        {
          title: "Venelaina",
          description: "Unelmavene helposti lainalla rahoitettuna",
          path: "/lainat/venelaina",
          icon: Ship,
          minAmount: "5 000€",
          maxAmount: "200 000€"
        },
        {
          title: "Lomalaina",
          description: "Toteuta unelmalomat heti - maksa myöhemmin",
          path: "/lainat/lomalaina",
          icon: Plane,
          minAmount: "1 000€",
          maxAmount: "25 000€"
        },
        {
          title: "Häälaina",
          description: "Unelmahäät rahoituksella toteutettuna",
          path: "/lainat/haalaina",
          icon: Heart,
          minAmount: "2 000€",
          maxAmount: "50 000€"
        },
        {
          title: "Pienkonelaina",
          description: "Rahoitusta työkaluihin ja pienkoneisiin",
          path: "/lainat/pienkonelaina",
          icon: Wrench,
          isNew: true,
          minAmount: "1 000€",
          maxAmount: "25 000€"
        }
      ]
    },
    {
      title: "Asunto- ja remonttilainat",
      description: "Asumiseen ja asunnon kunnostukseen",
      products: [
        {
          title: "Asuntolaina",
          description: "Kilpailutettua asuntolainaa kotisi ostoon",
          path: "/lainat/asuntolaina",
          icon: Home,
          isPopular: true,
          minAmount: "50 000€",
          maxAmount: "1 000 000€"
        },
        {
          title: "Remonttilaina",
          description: "Rahoitusta kodin kunnostukseen ja sisustukseen",
          path: "/lainat/remonttilaina",
          icon: Hammer,
          minAmount: "5 000€",
          maxAmount: "100 000€"
        },
        {
          title: "Mökkilaina",
          description: "Kesämökin ostoon tai rakentamiseen",
          path: "/lainat/mokkilaina",
          icon: TreePine,
          minAmount: "20 000€",
          maxAmount: "500 000€"
        }
      ]
    },
    {
      title: "Erikoistuotteet",
      description: "Pienlainat ja muut erikoisratkaisut",
      products: [
        {
          title: "Pienlainat ja luottokortit",
          description: "Vertaile pienlainoja ja luottokortteja",
          path: "/lainat/pienlainat-luottokortit",
          icon: CreditCard,
          minAmount: "100€",
          maxAmount: "10 000€"
        },
        {
          title: "Ulosottolaskuri",
          description: "Laske ulosoton määrä ja selvitä velallisen oikeudet",
          path: "/lainat/ulosottolaskuri",
          icon: Calculator,
          isNew: true
        }
      ]
    }
  ];

  // Get popular products for featured section
  const popularProducts = loanCategories
    .flatMap(category => category.products)
    .filter(product => product.isPopular);

  const ProductCard = ({ product }: { product: LoanProduct }) => {
    const Icon = product.icon;
    
    return (
      <Card className="h-full hover:shadow-xl transition-all duration-300 border border-blue-200 hover:border-blue-400 bg-gradient-to-br from-white to-blue-50/30 group">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-3 rounded-xl group-hover:from-blue-200 group-hover:to-blue-300 transition-all">
                <Icon className="h-6 w-6 text-blue-700" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg text-blue-800 group-hover:text-blue-900 transition-colors">{product.title}</CardTitle>
                <div className="flex gap-2 mt-1">
                  {product.isPopular && (
                    <Badge variant="default" className="bg-green-100 text-green-800 text-xs border-green-200">
                      Suosittu
                    </Badge>
                  )}
                  {product.isNew && (
                    <Badge variant="default" className="bg-orange-100 text-orange-800 text-xs border-orange-200">
                      Uusi
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-gray-700 mb-4 text-sm leading-relaxed">
            {product.description}
          </p>
          {(product.minAmount || product.maxAmount) && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg mb-4 border border-blue-100">
              <p className="text-sm text-blue-800 font-semibold">
                Lainasumma: {product.minAmount} - {product.maxAmount}
              </p>
            </div>
          )}
          <Link to={product.path}>
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all text-center block shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
              Lue lisää →
            </div>
          </Link>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <EnhancedSeoMetadataComponent
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="https://lainasampo.fi/lainatuotteet"
        schema={seoData.schema}
      />
      
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50/50 to-purple-50/30 min-h-screen">
        <div className="container mx-auto max-w-6xl px-4">
          {/* Streamlined Hero section */}
          <div className="text-center py-8 md:py-12">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl p-6 md:p-8 mb-8 shadow-lg">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                Kaikki lainatuotteet yhdessä paikassa
              </h1>
              <p className="text-lg md:text-xl text-blue-100 mb-6 max-w-3xl mx-auto">
                Vertaile ja kilpailuta lainoja helposti. Löydä itsellesi sopivin lainatuote ja säästä rahaa.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <LainaCta 
                  text="Kilpailuta lainasi" 
                  size={isMobile ? "md" : "lg"}
                  variant="secondary" 
                  dataTestId="hero-cta-primary"
                />
                <div className="text-blue-200 text-sm">
                  ✓ Ilmainen vertailu ✓ Nopea käsittely ✓ Yli 20 lainanantajaa
                </div>
              </div>
            </div>
          </div>
          
          {/* Promoted loan types */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-blue-800 mb-3">
                Suosituimmat lainatuotteet
              </h2>
              <p className="text-gray-700 max-w-2xl mx-auto">
                Aloita vertailu näistä suosituimmista lainatuotteista tai tutki vaihtoehtoja alla
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="w-full">
                <PienlainatPromotion variant="default" />
              </div>
              <div className="w-full">
                <YrityslainatPromotion variant="default" />
              </div>
            </div>
            
            {/* Popular products showcase */}
            <div className="bg-gradient-to-r from-white to-blue-50/50 p-6 rounded-xl shadow-lg border border-blue-200 mb-8">
              <h3 className="text-xl font-bold text-blue-800 mb-4 text-center">Asiakkaiden suosikit</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {popularProducts.map((product, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg border border-blue-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <product.icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800">{product.title}</h4>
                        <p className="text-xs text-blue-600">{product.minAmount} - {product.maxAmount}</p>
                      </div>
                    </div>
                    <Link to={product.path}>
                      <div className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        Vertaile tarjouksia →
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Why compete section */}
          <div className="bg-gradient-to-r from-white to-blue-50 p-6 rounded-xl shadow-sm mb-8 border border-blue-200">
            <h2 className="text-xl md:text-2xl font-bold text-blue-800 mb-3">Miksi kilpailuttaa laina?</h2>
            <p className="text-gray-700 mb-6">
              Vertailemalla eri pankkien ja rahoituslaitosten tarjoamia lainoja voit säästää merkittäviä summia 
              lainan kokonaiskustannuksissa. Jopa 0,5% ero lainan korossa voi tarkoittaa tuhansia euroja säästöä laina-aikana.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-lg hover:shadow-md transition-all border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2 text-lg">Säästä aikaa</h3>
                <p className="text-gray-700">Täytä vain yksi hakemus ja saat tarjoukset useilta pankeilta ja rahoituslaitoksilta.</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-lg hover:shadow-md transition-all border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2 text-lg">Säästä rahaa</h3>
                <p className="text-gray-700">Kilpailuttamalla löydät edullisimman kokonaisratkaisun ja voit säästää jopa tuhansia euroja.</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-5 rounded-lg hover:shadow-md transition-all border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2 text-lg">Helppo vertailu</h3>
                <p className="text-gray-700">Vertaile lainatarjouksia selkeässä muodossa ja valitse itsellesi sopivin ratkaisu.</p>
              </div>
            </div>
          </div>

          {/* All loan products organized by categories */}
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-800 mb-2 text-center">
              Kaikki lainatuotteemme
            </h2>
            <p className="text-gray-700 text-center mb-8 max-w-2xl mx-auto">
              Löydä itsellesi sopiva lainatuote laajasta valikoimastamme. Kaikki lainat kilpailutettu 
              useista pankeista ja rahoituslaitoksista.
            </p>

            {loanCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-10">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 rounded-t-xl text-white">
                  <h3 className="text-xl md:text-2xl font-bold mb-2">
                    {category.title}
                  </h3>
                  <p className="text-blue-100">
                    {category.description}
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-white to-blue-50/30 p-6 rounded-b-xl shadow-lg border-x border-b border-blue-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {category.products.map((product, productIndex) => (
                      <ProductCard key={productIndex} product={product} />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Additional sections */}
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="hover:shadow-lg transition-shadow bg-gradient-to-br from-white to-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-blue-800">Uudet lainatuotteet</h3>
                  <p className="text-gray-700 mb-4">
                    Tutustu uusiin lainatuotteisiimme. Olemme lisänneet valikoimaamme monipuolisesti erilaisia rahoitusvaihtoehtoja 
                    erilaisiin tarpeisiin, kuten venelaina, moottoripyörälaina, mökkilaina, remonttilaina ja pienkonelaina.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Link to="/lainat/venelaina" className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm hover:bg-blue-200 transition-colors">
                      Venelaina
                    </Link>
                    <Link to="/lainat/moottoripyoralaina" className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm hover:bg-blue-200 transition-colors">
                      Moottoripyörälaina
                    </Link>
                    <Link to="/lainat/mokkilaina" className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm hover:bg-blue-200 transition-colors">
                      Mökkilaina
                    </Link>
                    <Link to="/lainat/caravanrahoitus" className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm hover:bg-blue-200 transition-colors">
                      Caravanrahoitus
                    </Link>
                    <Link to="/lainat/remonttilaina" className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm hover:bg-blue-200 transition-colors">
                      Remonttilaina
                    </Link>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow bg-gradient-to-br from-white to-green-50 border-green-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-green-800">Lue asiantuntija-artikkelimme</h3>
                  <p className="text-gray-700 mb-4">
                    Laadukkaista artikkeleistamme löydät hyödyllistä tietoa eri lainatuotteista ja rahoitusvaihtoehdoista. 
                    Asiantuntijamme auttavat sinua tekemään oikeita päätöksiä rahoituksen suhteen.
                  </p>
                  <ul className="space-y-2">
                    <li>
                      <Link to="/artikkelit/lainan-ottamisen-opas-2025" className="text-green-700 hover:underline flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        Lainan ottamisen opas 2025
                      </Link>
                    </li>
                    <li>
                      <Link to="/artikkelit/autolaina-ohjeet" className="text-green-700 hover:underline flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        Autolaina ohjeet
                      </Link>
                    </li>
                    <li>
                      <Link to="/artikkelit/yrittajan-rahoitusopas-2025-nain-haet-ja-saat-yrityslainaa" className="text-green-700 hover:underline flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        Yrittäjän rahoitusopas 2025
                      </Link>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <PartnersCarousel />
          
          {isMobile && (
            <div className="py-6 mb-8">
              <LainaCta 
                variant="full" 
                size="lg" 
                text="Kilpailuta lainasi nyt"
                dataTestId="lainatuotteet-mobile-bottom-cta"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LainatuotteetPage;
